/**
 * Provisioning-setup workflow: library-state read + corpus bootstrap plan/execute
 * + governed upload with bounded list polling and one post-terminal library-state refresh.
 * Enabled only after provisioning-setup access is authorized.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  executeProvisioningCorpusBootstrap,
  planProvisioningCorpusBootstrap,
  type ProvisioningCorpusBootstrapPlan,
} from "../api/cognitoryProvisioningCorpusBootstrapClient";
import {
  fetchProvisioningLibraryState,
  type ProvisioningLibraryStateSetupPhase,
} from "../api/cognitoryProvisioningLibraryStateClient";
import {
  fetchGovernedLibraryDocuments,
  type GovernedLibraryDocumentSummary,
} from "../api/cognitoryLibraryClient";
import { uploadGovernedLibraryPdf } from "../api/cognitoryLibraryUploadClient";
import {
  executeProvisioningProductInitialActivation,
  type ProvisioningProductInitialActivationSuccessDecision,
} from "../api/cognitoryProvisioningProductInitialActivationClient";
import {
  fetchProvisioningProductActivationReadiness,
  provisioningProductActivationNotReadyMessage,
  type ProvisioningProductActivationNotReadyReason,
} from "../api/cognitoryProvisioningProductActivationReadinessClient";
import { pollUploadIngestionUntilTerminal } from "../components/cognitoryLibraryUploadPolling";
import {
  createUploadAttemptLifecycle,
  resolveUploadScopedDocumentSummary,
  type UploadAttemptLifecycle,
} from "../components/cognitoryLibraryUploadLifecycle";
import { resolveUploadFailureMessage } from "../components/cognitoryLibraryUploadStage";

export type ProvisioningSetupLibraryStatePhase =
  | "idle"
  | "loading"
  | "resolved"
  | "library_state_conflict"
  | "unauthenticated"
  | "forbidden"
  | "invalid_request"
  | "system_not_eligible"
  | "temporary_failure"
  | "unable_to_verify";

export type ProvisioningSetupBootstrapUiPhase =
  | "hidden"
  | "idle"
  | "planning"
  | "review"
  | "executing";

export const PROVISIONING_SETUP_UPLOAD_ELIGIBLE_SETUP_PHASES = [
  "library_initialized_draft",
  "library_processing",
  "library_activation_ready",
] as const satisfies readonly ProvisioningLibraryStateSetupPhase[];

export type ProvisioningSetupActivationReadinessPhase =
  | "hidden"
  | "idle"
  | "loading"
  | "ready"
  | "not_ready"
  | "unauthenticated"
  | "forbidden"
  | "invalid_request"
  | "system_not_eligible"
  | "temporary_failure"
  | "unable_to_verify";

export type ProvisioningSetupActivationTerminalDecision =
  ProvisioningProductInitialActivationSuccessDecision;

export type ProvisioningSetupActivationTerminalResult = {
  cognitoryId: string;
  decision: ProvisioningSetupActivationTerminalDecision;
};

export function isProvisioningSetupActivationReviewEligible(input: {
  enabled: boolean;
  libraryStatePhase: ProvisioningSetupLibraryStatePhase;
  setupPhase: ProvisioningLibraryStateSetupPhase | null;
  activationTerminal: ProvisioningSetupActivationTerminalResult | null;
}): boolean {
  if (!input.enabled || input.activationTerminal != null) {
    return false;
  }
  if (input.libraryStatePhase !== "resolved" || input.setupPhase == null) {
    return false;
  }
  return input.setupPhase === "library_activation_ready";
}

export function isProvisioningSetupUploadEligible(input: {
  libraryStatePhase: ProvisioningSetupLibraryStatePhase;
  setupPhase: ProvisioningLibraryStateSetupPhase | null;
}): boolean {
  if (input.libraryStatePhase !== "resolved" || input.setupPhase == null) {
    return false;
  }
  return (
    PROVISIONING_SETUP_UPLOAD_ELIGIBLE_SETUP_PHASES as readonly string[]
  ).includes(input.setupPhase);
}

export type ProvisioningSetupWorkflowState = {
  libraryStatePhase: ProvisioningSetupLibraryStatePhase;
  setupPhase: ProvisioningLibraryStateSetupPhase | null;
  libraryStateRequestId: string | null;
  bootstrapAccepted: boolean;
  bootstrapRefreshFailed: boolean;
  bootstrapUiPhase: ProvisioningSetupBootstrapUiPhase;
  bootstrapPlan: ProvisioningCorpusBootstrapPlan | null;
  bootstrapErrorMessage: string | null;
  uploadEligible: boolean;
  uploadAccepted: boolean;
  uploadBusy: boolean;
  uploadActiveFilename: string | null;
  uploadActiveTitle: string | null;
  uploadTrackedDocument: GovernedLibraryDocumentSummary | null;
  uploadPollTimedOut: boolean;
  uploadErrorMessage: string | null;
  postUploadLibraryStateRefreshFailed: boolean;
  activationReadinessPhase: ProvisioningSetupActivationReadinessPhase;
  activationNotReadyReason: ProvisioningProductActivationNotReadyReason | null;
  activationReadinessRequestId: string | null;
  activationTerminal: ProvisioningSetupActivationTerminalResult | null;
  activationBusy: boolean;
  activationNotReadyMessage: string | null;
  activationErrorMessage: string | null;
  refreshLibraryState: () => void;
  reviewActivationReadiness: () => void;
  startBootstrapPlan: () => Promise<void>;
  executeBootstrap: () => Promise<void>;
  cancelBootstrapReview: () => void;
  submitUpload: (input: { file: File; title?: string }) => Promise<void>;
  executeActivation: () => Promise<void>;
};

function applyLibraryStateResult(
  result: Awaited<ReturnType<typeof fetchProvisioningLibraryState>>,
  setters: {
    setLibraryStatePhase: (p: ProvisioningSetupLibraryStatePhase) => void;
    setSetupPhase: (p: ProvisioningLibraryStateSetupPhase | null) => void;
    setLibraryStateRequestId: (id: string | null) => void;
    setBootstrapPlan: (plan: ProvisioningCorpusBootstrapPlan | null) => void;
    setBootstrapUiPhase: (p: ProvisioningSetupBootstrapUiPhase) => void;
    setBootstrapRefreshFailed: (v: boolean) => void;
  },
): void {
  setters.setLibraryStateRequestId(result.requestId);

  if (result.kind === "resolved") {
    setters.setLibraryStatePhase("resolved");
    setters.setSetupPhase(result.setupPhase);
    setters.setBootstrapRefreshFailed(false);
    if (result.setupPhase !== "bootstrap_required") {
      setters.setBootstrapPlan(null);
      setters.setBootstrapUiPhase("hidden");
    }
    return;
  }

  setters.setSetupPhase(null);
  if (result.kind === "library_state_conflict") {
    setters.setLibraryStatePhase("library_state_conflict");
    return;
  }
  if (
    result.kind === "unauthenticated" ||
    result.kind === "forbidden" ||
    result.kind === "invalid_request" ||
    result.kind === "system_not_eligible" ||
    result.kind === "temporary_failure" ||
    result.kind === "unable_to_verify"
  ) {
    setters.setLibraryStatePhase(result.kind);
  }
}

function mapUploadClientError(input: {
  kind: string;
  filename: string;
}): string {
  if (input.kind === "forbidden") {
    return resolveUploadFailureMessage({
      filename: input.filename,
      kind: "forbidden",
    });
  }
  if (input.kind === "pdf_rejected") {
    return resolveUploadFailureMessage({
      filename: input.filename,
      kind: "pdf_rejected",
    });
  }
  if (input.kind === "document_collision") {
    return resolveUploadFailureMessage({
      filename: input.filename,
      kind: "document_collision",
    });
  }
  if (input.kind === "invalid_request") {
    return resolveUploadFailureMessage({
      filename: input.filename,
      kind: "unknown",
    });
  }
  return resolveUploadFailureMessage({
    filename: input.filename,
    kind: "network",
  });
}

export function useProvisioningSetupWorkflow(input: {
  enabled: boolean;
  cognitoryId: string | null;
}): ProvisioningSetupWorkflowState {
  const requestGenerationRef = useRef(0);
  const uploadOpGenerationRef = useRef(0);
  const activationReadinessGenerationRef = useRef(0);
  const activationOpGenerationRef = useRef(0);
  const executeInFlightRef = useRef(false);
  const uploadInFlightRef = useRef(false);
  const activationInFlightRef = useRef(false);
  const uploadPollingAbortRef = useRef<AbortController | null>(null);
  const uploadLifecycleRef = useRef<UploadAttemptLifecycle | null>(null);

  const [libraryStatePhase, setLibraryStatePhase] =
    useState<ProvisioningSetupLibraryStatePhase>("idle");
  const [setupPhase, setSetupPhase] =
    useState<ProvisioningLibraryStateSetupPhase | null>(null);
  const [libraryStateRequestId, setLibraryStateRequestId] = useState<
    string | null
  >(null);
  const [bootstrapAccepted, setBootstrapAccepted] = useState(false);
  const [bootstrapRefreshFailed, setBootstrapRefreshFailed] = useState(false);
  const [bootstrapUiPhase, setBootstrapUiPhase] =
    useState<ProvisioningSetupBootstrapUiPhase>("hidden");
  const [bootstrapPlan, setBootstrapPlan] =
    useState<ProvisioningCorpusBootstrapPlan | null>(null);
  const [bootstrapErrorMessage, setBootstrapErrorMessage] = useState<
    string | null
  >(null);
  const [uploadAccepted, setUploadAccepted] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadActiveFilename, setUploadActiveFilename] = useState<
    string | null
  >(null);
  const [uploadActiveTitle, setUploadActiveTitle] = useState<string | null>(
    null,
  );
  const [uploadTrackedDocument, setUploadTrackedDocument] =
    useState<GovernedLibraryDocumentSummary | null>(null);
  const [uploadPollTimedOut, setUploadPollTimedOut] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null,
  );
  const [
    postUploadLibraryStateRefreshFailed,
    setPostUploadLibraryStateRefreshFailed,
  ] = useState(false);
  const [activationReadinessPhase, setActivationReadinessPhase] =
    useState<ProvisioningSetupActivationReadinessPhase>("hidden");
  const [activationNotReadyReason, setActivationNotReadyReason] =
    useState<ProvisioningProductActivationNotReadyReason | null>(null);
  const [activationReadinessRequestId, setActivationReadinessRequestId] =
    useState<string | null>(null);
  const [activationTerminal, setActivationTerminal] =
    useState<ProvisioningSetupActivationTerminalResult | null>(null);
  const [activationBusy, setActivationBusy] = useState(false);
  const [activationErrorMessage, setActivationErrorMessage] = useState<
    string | null
  >(null);

  const resetActivationSession = useCallback(() => {
    activationReadinessGenerationRef.current += 1;
    activationOpGenerationRef.current += 1;
    activationInFlightRef.current = false;
    setActivationReadinessPhase("hidden");
    setActivationNotReadyReason(null);
    setActivationReadinessRequestId(null);
    setActivationBusy(false);
    setActivationErrorMessage(null);
  }, []);

  const resetUploadSession = useCallback(() => {
    uploadPollingAbortRef.current?.abort();
    uploadPollingAbortRef.current = null;
    uploadLifecycleRef.current = null;
    setUploadBusy(false);
    setUploadActiveFilename(null);
    setUploadActiveTitle(null);
    setUploadTrackedDocument(null);
    setUploadPollTimedOut(false);
    setUploadErrorMessage(null);
    uploadInFlightRef.current = false;
  }, []);

  const resetWorkflowState = useCallback(() => {
    setLibraryStatePhase("idle");
    setSetupPhase(null);
    setLibraryStateRequestId(null);
    setBootstrapAccepted(false);
    setBootstrapRefreshFailed(false);
    setBootstrapUiPhase("hidden");
    setBootstrapPlan(null);
    setBootstrapErrorMessage(null);
    setUploadAccepted(false);
    setPostUploadLibraryStateRefreshFailed(false);
    executeInFlightRef.current = false;
    uploadOpGenerationRef.current += 1;
    resetUploadSession();
    resetActivationSession();
  }, [resetUploadSession, resetActivationSession]);

  const loadLibraryState = useCallback(
    (
      cognitoryId: string,
      options?: {
        afterBootstrapAccept?: boolean;
        afterUploadTerminal?: boolean;
      },
    ) => {
      const opGen = ++requestGenerationRef.current;
      setLibraryStatePhase("loading");
      if (!options?.afterBootstrapAccept && !options?.afterUploadTerminal) {
        setBootstrapRefreshFailed(false);
        setPostUploadLibraryStateRefreshFailed(false);
      }

      void fetchProvisioningLibraryState({ cognitoryId }).then(result => {
        if (opGen !== requestGenerationRef.current) {
          return;
        }
        if (options?.afterBootstrapAccept && result.kind !== "resolved") {
          setBootstrapRefreshFailed(true);
        }
        if (options?.afterUploadTerminal && result.kind !== "resolved") {
          setPostUploadLibraryStateRefreshFailed(true);
        }
        applyLibraryStateResult(result, {
          setLibraryStatePhase,
          setSetupPhase,
          setLibraryStateRequestId,
          setBootstrapPlan,
          setBootstrapUiPhase,
          setBootstrapRefreshFailed,
        });
      });
    },
    [],
  );

  useEffect(() => {
    if (!input.enabled || !input.cognitoryId) {
      requestGenerationRef.current += 1;
      resetWorkflowState();
      return;
    }

    resetWorkflowState();
    const cognitoryId = input.cognitoryId;
    setActivationTerminal(current =>
      current != null && current.cognitoryId !== cognitoryId ? null : current,
    );
    loadLibraryState(cognitoryId);

    return () => {
      requestGenerationRef.current += 1;
      uploadOpGenerationRef.current += 1;
      activationReadinessGenerationRef.current += 1;
      activationOpGenerationRef.current += 1;
      executeInFlightRef.current = false;
      activationInFlightRef.current = false;
      uploadPollingAbortRef.current?.abort();
      uploadPollingAbortRef.current = null;
      uploadInFlightRef.current = false;
    };
  }, [input.enabled, input.cognitoryId, loadLibraryState, resetWorkflowState]);

  const refreshLibraryState = useCallback(() => {
    if (!input.enabled || !input.cognitoryId) {
      return;
    }
    loadLibraryState(input.cognitoryId);
  }, [input.enabled, input.cognitoryId, loadLibraryState]);

  const loadActivationReadiness = useCallback((cognitoryId: string) => {
    const opGen = ++activationReadinessGenerationRef.current;
    setActivationErrorMessage(null);
    setActivationNotReadyReason(null);
    setActivationReadinessRequestId(null);
    setActivationReadinessPhase("loading");

    void fetchProvisioningProductActivationReadiness({ cognitoryId }).then(
      result => {
        if (opGen !== activationReadinessGenerationRef.current) {
          return;
        }
        if (result.kind === "ready") {
          setActivationReadinessRequestId(result.requestId);
          setActivationReadinessPhase("ready");
          return;
        }
        if (result.kind === "not_ready") {
          setActivationReadinessRequestId(result.requestId);
          setActivationNotReadyReason(result.reason);
          setActivationReadinessPhase("not_ready");
          return;
        }
        setActivationReadinessRequestId(result.requestId);
        if (
          result.kind === "unauthenticated" ||
          result.kind === "forbidden" ||
          result.kind === "invalid_request" ||
          result.kind === "system_not_eligible" ||
          result.kind === "temporary_failure" ||
          result.kind === "unable_to_verify"
        ) {
          setActivationReadinessPhase(result.kind);
          return;
        }
        setActivationReadinessPhase("unable_to_verify");
      },
    );
  }, []);

  useEffect(() => {
    if (
      !isProvisioningSetupActivationReviewEligible({
        enabled: input.enabled,
        libraryStatePhase,
        setupPhase,
        activationTerminal,
      }) ||
      !input.cognitoryId
    ) {
      if (activationTerminal == null) {
        activationReadinessGenerationRef.current += 1;
        setActivationReadinessPhase("hidden");
        setActivationNotReadyReason(null);
        setActivationReadinessRequestId(null);
        setActivationErrorMessage(null);
      }
      return;
    }
    loadActivationReadiness(input.cognitoryId);
  }, [
    input.enabled,
    input.cognitoryId,
    libraryStatePhase,
    setupPhase,
    activationTerminal,
    loadActivationReadiness,
  ]);

  const reviewActivationReadiness = useCallback(() => {
    if (
      !isProvisioningSetupActivationReviewEligible({
        enabled: input.enabled,
        libraryStatePhase,
        setupPhase,
        activationTerminal,
      }) ||
      !input.cognitoryId
    ) {
      return;
    }
    loadActivationReadiness(input.cognitoryId);
  }, [
    input.enabled,
    input.cognitoryId,
    libraryStatePhase,
    setupPhase,
    activationTerminal,
    loadActivationReadiness,
  ]);

  const executeActivation = useCallback(async () => {
    if (!input.enabled || !input.cognitoryId) {
      return;
    }
    if (activationTerminal != null) {
      return;
    }
    if (activationInFlightRef.current) {
      return;
    }
    if (activationReadinessPhase !== "ready") {
      return;
    }

    const cognitoryId = input.cognitoryId;
    activationInFlightRef.current = true;
    const opGen = ++activationOpGenerationRef.current;
    setActivationBusy(true);
    setActivationErrorMessage(null);

    const result = await executeProvisioningProductInitialActivation({
      cognitoryId,
    });

    if (opGen !== activationOpGenerationRef.current) {
      return;
    }

    activationInFlightRef.current = false;
    setActivationBusy(false);

    if (activationTerminal != null) {
      return;
    }

    if (result.kind === "succeeded") {
      setActivationTerminal({
        cognitoryId,
        decision: result.decision,
      });
      activationReadinessGenerationRef.current += 1;
      setActivationReadinessPhase("hidden");
      setActivationNotReadyReason(null);
      setActivationReadinessRequestId(null);
      return;
    }

    if (result.kind === "not_ready") {
      setActivationErrorMessage(
        "Activation is not ready yet. Review readiness and try again.",
      );
      loadActivationReadiness(cognitoryId);
      return;
    }
    if (result.kind === "state_conflict") {
      setActivationErrorMessage(
        "Activation could not complete because setup state changed. Refresh and try again.",
      );
      return;
    }
    if (result.kind === "forbidden") {
      setActivationErrorMessage(
        "You are not authorized to activate this Cognitory.",
      );
      return;
    }
    if (result.kind === "unauthenticated") {
      setActivationErrorMessage("Sign in to activate this Cognitory.");
      return;
    }
    if (result.kind === "invalid_request") {
      setActivationErrorMessage("This activation request is invalid.");
      return;
    }
    setActivationErrorMessage(
      "Unable to activate this Cognitory right now. Try again later.",
    );
  }, [
    input.enabled,
    input.cognitoryId,
    activationTerminal,
    activationReadinessPhase,
    loadActivationReadiness,
  ]);

  const startBootstrapPlan = useCallback(async () => {
    if (!input.enabled || !input.cognitoryId) {
      return;
    }
    const cognitoryId = input.cognitoryId;
    setBootstrapErrorMessage(null);
    setBootstrapPlan(null);
    const opGen = ++requestGenerationRef.current;
    setBootstrapUiPhase("planning");

    const result = await planProvisioningCorpusBootstrap({ cognitoryId });
    if (opGen !== requestGenerationRef.current) {
      return;
    }

    if (result.kind === "planned") {
      setBootstrapPlan(result.plan);
      setBootstrapUiPhase("review");
      return;
    }

    setBootstrapUiPhase(
      setupPhase === "bootstrap_required" ? "idle" : "hidden",
    );
    if (result.kind === "validation_failed") {
      setBootstrapErrorMessage(
        "The bootstrap plan expired or changed. Try again.",
      );
    } else {
      setBootstrapErrorMessage(
        "Unable to prepare the bootstrap plan. Try again.",
      );
    }
  }, [input.enabled, input.cognitoryId, setupPhase]);

  const executeBootstrap = useCallback(async () => {
    if (!input.enabled || !input.cognitoryId || !bootstrapPlan) {
      return;
    }
    if (executeInFlightRef.current) {
      return;
    }
    executeInFlightRef.current = true;
    const cognitoryId = input.cognitoryId;
    setBootstrapErrorMessage(null);
    const opGen = ++requestGenerationRef.current;
    setBootstrapUiPhase("executing");

    const result = await executeProvisioningCorpusBootstrap({
      cognitoryId,
      planTimestamp: bootstrapPlan.planTimestamp,
      writeSetHash: bootstrapPlan.writeSetHash,
    });

    executeInFlightRef.current = false;

    if (opGen !== requestGenerationRef.current) {
      return;
    }

    if (
      result.kind === "bootstrapped" ||
      result.kind === "already_bootstrapped"
    ) {
      setBootstrapAccepted(true);
      setBootstrapPlan(null);
      setBootstrapUiPhase("hidden");
      loadLibraryState(cognitoryId, { afterBootstrapAccept: true });
      return;
    }

    setBootstrapUiPhase("review");
    if (result.kind === "validation_failed") {
      setBootstrapPlan(null);
      setBootstrapUiPhase("idle");
      setBootstrapErrorMessage(
        "The bootstrap plan expired or changed. Start over.",
      );
    } else if (
      result.kind === "bootstrap_state_drift" ||
      result.kind === "partial_bootstrap_state"
    ) {
      setBootstrapErrorMessage(
        "Bootstrap state conflict. Refresh library state before trying again.",
      );
    } else {
      setBootstrapErrorMessage(
        "Unable to run bootstrap right now. Try again later.",
      );
    }
  }, [input.enabled, input.cognitoryId, bootstrapPlan, loadLibraryState]);

  const cancelBootstrapReview = useCallback(() => {
    setBootstrapPlan(null);
    setBootstrapErrorMessage(null);
    setBootstrapUiPhase(
      setupPhase === "bootstrap_required" ? "idle" : "hidden",
    );
  }, [setupPhase]);

  const submitUpload = useCallback(
    async (uploadInput: { file: File; title?: string }) => {
      if (!input.enabled || !input.cognitoryId) {
        return;
      }
      if (uploadInFlightRef.current) {
        return;
      }
      if (
        !isProvisioningSetupUploadEligible({
          libraryStatePhase,
          setupPhase,
        })
      ) {
        return;
      }

      const cognitoryId = input.cognitoryId;
      const opGen = ++uploadOpGenerationRef.current;
      uploadInFlightRef.current = true;
      setUploadBusy(true);
      setUploadErrorMessage(null);
      setUploadPollTimedOut(false);
      setPostUploadLibraryStateRefreshFailed(false);
      setUploadActiveFilename(uploadInput.file.name);
      setUploadActiveTitle(
        uploadInput.title?.trim() ? uploadInput.title.trim() : null,
      );
      setUploadTrackedDocument(null);

      uploadPollingAbortRef.current?.abort();
      uploadPollingAbortRef.current = null;

      const trimmedUploadTitle = uploadInput.title?.trim();
      const uploadResult = await uploadGovernedLibraryPdf({
        cognitoryId,
        file: uploadInput.file,
        ...(trimmedUploadTitle ? { title: trimmedUploadTitle } : {}),
      });

      if (opGen !== uploadOpGenerationRef.current) {
        uploadInFlightRef.current = false;
        setUploadBusy(false);
        return;
      }

      if (uploadResult.kind !== "uploaded") {
        uploadInFlightRef.current = false;
        setUploadBusy(false);
        setUploadErrorMessage(
          mapUploadClientError({
            kind: uploadResult.kind,
            filename: uploadInput.file.name,
          }),
        );
        return;
      }

      setUploadAccepted(true);
      const lifecycle = createUploadAttemptLifecycle({
        documentId: uploadResult.documentId,
        documentVersionId: uploadResult.documentVersionId,
      });
      uploadLifecycleRef.current = lifecycle;
      const trackedLifecycles = new Map([[uploadResult.documentId, lifecycle]]);
      const trackedIds = new Set([uploadResult.documentId]);

      const abortController = new AbortController();
      uploadPollingAbortRef.current = abortController;

      const pollResult = await pollUploadIngestionUntilTerminal({
        trackedDocumentIds: trackedIds,
        trackedUploadLifecycles: trackedLifecycles,
        fetchDocuments: async () => {
          const listed = await fetchGovernedLibraryDocuments({ cognitoryId });
          if (listed.kind !== "listed") {
            return [];
          }
          return listed.documents;
        },
        sleep: ms =>
          new Promise(resolve => {
            window.setTimeout(resolve, ms);
          }),
        signal: abortController.signal,
        onDocuments: documents => {
          if (opGen !== uploadOpGenerationRef.current) {
            return;
          }
          const match = documents.find(
            entry => entry.documentId === uploadResult.documentId,
          );
          if (!match) {
            return;
          }
          const scoped = resolveUploadScopedDocumentSummary(match, lifecycle);
          setUploadTrackedDocument(scoped ?? match);
        },
      });

      uploadInFlightRef.current = false;
      setUploadBusy(false);

      if (opGen !== uploadOpGenerationRef.current) {
        return;
      }

      if (pollResult.kind === "aborted") {
        return;
      }

      if (pollResult.kind === "timeout") {
        setUploadPollTimedOut(true);
        return;
      }

      if (pollResult.kind === "all_terminal") {
        loadLibraryState(cognitoryId, { afterUploadTerminal: true });
      }
    },
    [
      input.enabled,
      input.cognitoryId,
      libraryStatePhase,
      setupPhase,
      loadLibraryState,
    ],
  );

  const showBootstrap =
    input.enabled && setupPhase === "bootstrap_required" && !bootstrapAccepted;

  const effectiveBootstrapUi: ProvisioningSetupBootstrapUiPhase = showBootstrap
    ? bootstrapUiPhase === "hidden"
      ? "idle"
      : bootstrapUiPhase
    : "hidden";

  const uploadEligible = isProvisioningSetupUploadEligible({
    libraryStatePhase,
    setupPhase,
  });

  const activationReviewEligible = isProvisioningSetupActivationReviewEligible({
    enabled: input.enabled,
    libraryStatePhase,
    setupPhase,
    activationTerminal,
  });

  const activationNotReadyMessage =
    activationNotReadyReason != null
      ? provisioningProductActivationNotReadyMessage(activationNotReadyReason)
      : null;

  return {
    libraryStatePhase,
    setupPhase,
    libraryStateRequestId,
    bootstrapAccepted,
    bootstrapRefreshFailed,
    bootstrapUiPhase: effectiveBootstrapUi,
    bootstrapPlan,
    bootstrapErrorMessage,
    uploadEligible,
    uploadAccepted,
    uploadBusy,
    uploadActiveFilename,
    uploadActiveTitle,
    uploadTrackedDocument,
    uploadPollTimedOut,
    uploadErrorMessage,
    postUploadLibraryStateRefreshFailed,
    activationReadinessPhase:
      activationTerminal != null
        ? "hidden"
        : activationReviewEligible
          ? activationReadinessPhase
          : "hidden",
    activationNotReadyReason,
    activationReadinessRequestId,
    activationTerminal,
    activationBusy,
    activationNotReadyMessage,
    activationErrorMessage,
    refreshLibraryState,
    reviewActivationReadiness,
    startBootstrapPlan,
    executeBootstrap,
    cancelBootstrapReview,
    submitUpload,
    executeActivation,
  };
}
